import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Loader2,
  ToggleLeft,
  ToggleRight,
  CheckCircle,
  MapPin,
  FileText,
  Hash,
  Globe,
  Users,
  Calendar,
  Clock,
  Trash2,
  Save,
  Coins,
  Plus,
  X,
  Pencil,
} from 'lucide-react';
import { FormModal, MODAL_COLORS, useModal } from '@/components/modal';
import { useUpdateHospital, useDeactivateHospital, useHospitalBonuses, useCreateHospitalBonus, useUpdateHospitalBonus, useDeleteHospitalBonus } from '../hooks';
import { useActiveEmploymentRoles, useEmploymentRoleRates } from '@/features/admin';
import { localizedName } from '@/utils/localized';
import { triggerHapticFeedback } from '../../admin/utils/adminAnimations';
import toast from 'react-hot-toast';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import type { Hospital, HospitalBonus } from '../types';

const cardStyle = {
  backgroundColor: MODAL_COLORS.interactive,
  border: `1px solid ${MODAL_COLORS.border}`,
};

const actionBtnStyle = {
  border: `1px solid ${MODAL_COLORS.border}`,
};

const inputStyle = {
  backgroundColor: MODAL_COLORS.surface,
  border: `1px solid ${MODAL_COLORS.border}`,
  color: MODAL_COLORS.text,
};

interface HospitalDetailModalProps {
  hospital: Hospital;
  onClose: () => void;
}

export function HospitalDetailModal({ hospital, onClose }: HospitalDetailModalProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const sym = useCurrencySymbol();
  const { openConfirm } = useModal();
  const updateMutation = useUpdateHospital();
  const deleteMutation = useDeactivateHospital();
  const [isOpen, setIsOpen] = useState(true);
  const requestClose = useCallback(() => setIsOpen(false), []);

  // Hospital bonuses
  const { data: bonuses, isLoading: isLoadingBonuses } = useHospitalBonuses(hospital.id);
  const { data: employmentRoles } = useActiveEmploymentRoles();
  const createBonusMutation = useCreateHospitalBonus();
  const updateBonusMutation = useUpdateHospitalBonus();
  const deleteBonusMutation = useDeleteHospitalBonus();

  // Bonus add form state
  const [showAddBonus, setShowAddBonus] = useState(false);
  const [addRoleId, setAddRoleId] = useState('');
  const [addRateId, setAddRateId] = useState('');
  const [addBonusAmount, setAddBonusAmount] = useState('');

  // Fetch rates for selected role in add form
  const { data: addRoleRates } = useEmploymentRoleRates(addRoleId || undefined);

  // Bonus edit form state
  const [editingBonus, setEditingBonus] = useState<HospitalBonus | null>(null);
  const [editBonusAmount, setEditBonusAmount] = useState('');

  const resetAddBonus = () => {
    setAddRoleId('');
    setAddRateId('');
    setAddBonusAmount('');
  };

  const handleAddBonus = async () => {
    if (!addRateId || !addBonusAmount) return;
    try {
      triggerHapticFeedback();
      await createBonusMutation.mutateAsync({
        hospitalId: hospital.id,
        data: {
          employmentRoleRateId: addRateId,
          bonusAmount: parseFloat(addBonusAmount),
        },
      });
      toast.success(t('hospitals.bonuses.createSuccess'));
      resetAddBonus();
      setShowAddBonus(false);
    } catch {
      toast.error(t('hospitals.bonuses.createFailed'));
    }
  };

  const startEditBonus = (b: HospitalBonus) => {
    setEditingBonus(b);
    setEditBonusAmount(String(b.bonusAmount));
    setShowAddBonus(false);
  };

  const cancelEditBonus = () => {
    setEditingBonus(null);
    setEditBonusAmount('');
  };

  const handleUpdateBonus = async () => {
    if (!editingBonus || !editBonusAmount) return;
    try {
      triggerHapticFeedback();
      await updateBonusMutation.mutateAsync({
        hospitalId: hospital.id,
        bonusId: editingBonus.id,
        data: {
          bonusAmount: parseFloat(editBonusAmount),
        },
      });
      toast.success(t('hospitals.bonuses.updateSuccess'));
      cancelEditBonus();
    } catch {
      toast.error(t('hospitals.bonuses.updateFailed'));
    }
  };

  const handleDeleteBonus = (bonusId: string) => {
    triggerHapticFeedback();
    openConfirm({
      title: t('hospitals.bonuses.delete'),
      message: t('common.confirmDelete', 'Are you sure?'),
      variant: 'danger',
      confirmLabel: t('common.delete'),
      onConfirm: async () => {
        await deleteBonusMutation.mutateAsync({ hospitalId: hospital.id, bonusId });
        toast.success(t('hospitals.bonuses.deleteSuccess'));
      },
    });
  };

  // Editable fields
  const [name, setName] = useState(hospital.name);
  const [nameEn, setNameEn] = useState(hospital.nameEn ?? '');
  const [city, setCity] = useState(hospital.city);
  const [cityEn, setCityEn] = useState(hospital.cityEn ?? '');

  const hasChanges =
    name !== hospital.name ||
    nameEn !== (hospital.nameEn ?? '') ||
    city !== hospital.city ||
    cityEn !== (hospital.cityEn ?? '');

  const handleSave = async () => {
    if (!name.trim() || !city.trim()) return;
    try {
      triggerHapticFeedback();
      await updateMutation.mutateAsync({
        id: hospital.id,
        data: {
          name: name.trim(),
          nameEn: nameEn.trim() || undefined,
          city: city.trim(),
          cityEn: cityEn.trim() || undefined,
        },
      });
      toast.success(t('hospitals.updated'));
      requestClose();
    } catch {
      toast.error(t('hospitals.updateFailed'));
    }
  };

  const handleToggleActive = async () => {
    try {
      triggerHapticFeedback();
      await updateMutation.mutateAsync({
        id: hospital.id,
        data: { isActive: !hospital.isActive },
      });
      toast.success(
        t(hospital.isActive ? 'hospitals.deactivateSuccess' : 'hospitals.activateSuccess')
      );
    } catch {
      toast.error(t('hospitals.updateFailed'));
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={requestClose}
      onExitComplete={onClose}
      title={hospital.name}
      maxWidth="max-w-lg"
    >
      <div className="space-y-2">
        {/* Status */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 opacity-60" />
              <span className="text-sm font-bold">{t('hospitals.status')}</span>
              <span className="text-sm font-semibold opacity-70">
                {hospital.isActive ? t('hospitals.active') : t('hospitals.inactive')}
              </span>
            </div>
            <button
              onClick={handleToggleActive}
              disabled={updateMutation.isPending}
              className="flex items-center gap-1.5 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors disabled:opacity-50 cursor-pointer"
              style={actionBtnStyle}
            >
              {updateMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : hospital.isActive ? (
                <>
                  <ToggleRight className="w-3.5 h-3.5" />
                  {t('hospitals.inactive')}
                </>
              ) : (
                <>
                  <ToggleLeft className="w-3.5 h-3.5" />
                  {t('hospitals.active')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl p-2 text-center" style={cardStyle}>
            <Users className="w-5 h-5 mx-auto mb-1 opacity-60" />
            <p className="text-xl font-bold">{hospital.therapistCount ?? 0}</p>
            <p className="text-xs font-semibold opacity-60">{t('hospitals.employees')}</p>
          </div>
          <div className="rounded-xl p-2 text-center" style={cardStyle}>
            <FileText className="w-5 h-5 mx-auto mb-1 opacity-60" />
            <p className="text-xl font-bold">{hospital.reportCount ?? 0}</p>
            <p className="text-xs font-semibold opacity-60">{t('reports.totalReports')}</p>
          </div>
        </div>

        {/* Code (read-only) */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center gap-2 text-sm">
            <Hash className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('hospitals.code')}:</span>
            <span className="opacity-70 font-mono">{hospital.code}</span>
          </div>
        </div>

        {/* Editable fields */}
        <div className="rounded-xl p-3 space-y-2.5" style={cardStyle}>
          {/* Name */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
              <Building2 className="w-3.5 h-3.5" />
              {t('hospitals.name')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={inputStyle}
            />
          </div>

          {/* Name (English) */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
              <Building2 className="w-3.5 h-3.5" />
              {t('hospitals.nameEn')}
            </label>
            <input
              type="text"
              value={nameEn}
              onChange={(e) => setNameEn(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={inputStyle}
            />
          </div>

          {/* City */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
              <MapPin className="w-3.5 h-3.5" />
              {t('hospitals.city')}
            </label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={inputStyle}
            />
          </div>

          {/* City (English) */}
          <div className="space-y-1">
            <label className="flex items-center gap-1.5 text-xs font-bold opacity-70">
              <Globe className="w-3.5 h-3.5" />
              {t('hospitals.cityEn')}
            </label>
            <input
              type="text"
              value={cityEn}
              onChange={(e) => setCityEn(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400"
              style={inputStyle}
            />
          </div>

          {/* Hidden fields: Address, Department, Contact Name/Phone/Email, Equipment Notes, Notes */}
        </div>

        {/* Save button */}
        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={updateMutation.isPending || !name.trim() || !city.trim()}
            className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold transition-transform cursor-pointer disabled:opacity-50"
            style={{
              backgroundColor: MODAL_COLORS.header,
              border: `1px solid ${MODAL_COLORS.border}`,
              color: '#fff',
            }}
          >
            {updateMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {t('common.save')}
          </button>
        )}

        {/* Hospital Bonuses */}
        <div className="rounded-xl p-3" style={cardStyle}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Coins className="w-4 h-4 opacity-60" />
              <span className="text-sm font-bold">{t('hospitals.bonuses.title')}</span>
            </div>
            <button
              onClick={() => {
                triggerHapticFeedback();
                setShowAddBonus(!showAddBonus);
                cancelEditBonus();
              }}
              className="flex items-center gap-1.5 px-2.5 py-1 glass-surface rounded-lg text-xs font-bold hover:bg-white/10 transition-colors cursor-pointer"
              style={actionBtnStyle}
            >
              {showAddBonus ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
              {showAddBonus ? t('common.cancel') : t('hospitals.bonuses.add')}
            </button>
          </div>

          {/* Add Bonus Form */}
          {showAddBonus && (
            <div className="space-y-2 mb-2 p-2 rounded-lg" style={{ backgroundColor: MODAL_COLORS.surface }}>
              <select
                value={addRoleId}
                onChange={(e) => {
                  setAddRoleId(e.target.value);
                  setAddRateId('');
                }}
                className="w-full p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
              >
                <option value="">{t('hospitals.bonuses.selectRole')}</option>
                {(employmentRoles || []).map((role) => (
                  <option key={role.id} value={role.id}>
                    {localizedName(role, lang)}
                  </option>
                ))}
              </select>
              {addRoleId && (
                <select
                  value={addRateId}
                  onChange={(e) => setAddRateId(e.target.value)}
                  className="w-full p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
                  style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
                >
                  <option value="">{t('hospitals.bonuses.selectReportType')}</option>
                  {(addRoleRates || []).map((rate) => (
                    <option key={rate.id} value={rate.id}>
                      {rate.reportType ? localizedName(rate.reportType, lang) : rate.reportTypeId} ({rate.ratePerUnit} {t('employmentRoles.nisPerUnit')})
                    </option>
                  ))}
                </select>
              )}
              <input
                type="number"
                value={addBonusAmount}
                onChange={(e) => setAddBonusAmount(e.target.value)}
                min={0}
                step={0.01}
                placeholder={t('hospitals.bonuses.amount')}
                className="w-full p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500"
                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
              />
              <button
                onClick={handleAddBonus}
                disabled={!addRateId || !addBonusAmount || createBonusMutation.isPending}
                className="w-full px-2.5 py-1.5 rounded-lg text-xs font-bold cursor-pointer disabled:opacity-50 text-white"
                style={{ backgroundColor: 'var(--color-primary-500)', ...actionBtnStyle }}
              >
                {createBonusMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : t('common.add')}
              </button>
            </div>
          )}

          {/* Edit Bonus Form */}
          {editingBonus && (
            <div className="space-y-2 mb-2 p-2 rounded-lg" style={{ backgroundColor: 'rgba(254, 249, 195, 0.5)' }}>
              <div
                className="p-1.5 rounded-lg text-sm font-semibold"
                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: MODAL_COLORS.surface }}
              >
                {localizedName(editingBonus.employmentRoleRate.employmentRole, lang)} &gt; {localizedName(editingBonus.employmentRoleRate.reportType, lang)}
              </div>
              <input
                type="number"
                value={editBonusAmount}
                onChange={(e) => setEditBonusAmount(e.target.value)}
                min={0}
                step={0.01}
                placeholder={t('hospitals.bonuses.amount')}
                className="w-full p-1.5 rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-primary-500"
                style={{ border: `1px solid ${MODAL_COLORS.border}`, backgroundColor: '#fff' }}
              />
              <div className="flex gap-2">
                <button
                  onClick={handleUpdateBonus}
                  disabled={!editBonusAmount || updateBonusMutation.isPending}
                  className="flex-1 px-2.5 py-1.5 rounded-lg text-xs font-bold cursor-pointer disabled:opacity-50 text-white"
                  style={{ backgroundColor: 'var(--color-primary-500)', ...actionBtnStyle }}
                >
                  {updateBonusMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : t('common.save')}
                </button>
                <button
                  onClick={cancelEditBonus}
                  className="px-2 py-1.5 rounded-lg text-xs font-bold cursor-pointer glass-surface"
                  style={actionBtnStyle}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* Bonuses List */}
          {isLoadingBonuses ? (
            <div className="flex justify-center py-3">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : (bonuses || []).length === 0 ? (
            <p className="text-xs opacity-50 text-center py-2">{t('hospitals.bonuses.noResults')}</p>
          ) : (
            <div className="space-y-1">
              {(bonuses || []).map((b) => (
                <div
                  key={b.id}
                  className="flex items-center justify-between py-1 px-2 rounded-lg"
                  style={{ backgroundColor: MODAL_COLORS.surface }}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Coins className="w-3 h-3 opacity-40 flex-shrink-0" />
                    <div className="min-w-0">
                      <span className="text-xs font-semibold truncate block">
                        {localizedName(b.employmentRoleRate.employmentRole, lang)} &gt; {localizedName(b.employmentRoleRate.reportType, lang)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-xs font-medium opacity-70">
                      +{b.bonusAmount} {sym} / {t('hospitals.bonuses.perReport')}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditBonus(b);
                      }}
                      className="p-1 rounded hover:bg-blue-100 cursor-pointer"
                    >
                      <Pencil className="w-3 h-3 text-blue-500" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteBonus(b.id);
                      }}
                      disabled={deleteBonusMutation.isPending}
                      className="p-1 rounded hover:bg-red-100 cursor-pointer disabled:opacity-50"
                    >
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Dates */}
        <div className="rounded-xl p-3 space-y-1" style={cardStyle}>
          <div className="flex items-center gap-2 text-xs">
            <Calendar className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('hospitals.createdAt')}:</span>
            <span className="opacity-70">{new Date(hospital.createdAt).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Clock className="w-3.5 h-3.5 opacity-50" />
            <span className="font-bold">{t('hospitals.updatedAt')}:</span>
            <span className="opacity-70">{new Date(hospital.updatedAt).toLocaleDateString()}</span>
          </div>
        </div>

        {/* Delete */}
        <button
          onClick={() => {
            triggerHapticFeedback();
            openConfirm({
              title: t('hospitals.delete'),
              message: t('hospitals.deleteConfirm'),
              variant: 'danger',
              confirmLabel: t('common.delete'),
              onConfirm: async () => {
                await deleteMutation.mutateAsync(hospital.id);
                toast.success(t('hospitals.deleteSuccess'));
                requestClose();
              },
            });
          }}
          disabled={deleteMutation.isPending}
          className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-xl font-bold hover:translate-x-[1px] hover:translate-y-[1px] transition-transform cursor-pointer disabled:opacity-50"
          style={{
            backgroundColor: 'rgba(254, 226, 226, 0.6)',
            border: `1px solid ${MODAL_COLORS.border}`,
            color: '#B91C1C',
          }}
        >
          <Trash2 className="w-4 h-4" />
          {t('hospitals.delete')}
        </button>
      </div>
    </FormModal>
  );
}
